/**
 * Decision-Log / Entscheidungs-Protokoll (CompanyBrain Welle 3, BEN-107).
 * Hooks fuer Liste/Filter/Detail/Anlegen/Bearbeiten/Status + lokale KI-Hilfe (Vorschlag).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenant } from "./tenant";

export type DecisionLink = { kind?: string; ref?: string; label?: string; route?: string };

export type Decision = {
  id: string;
  user_sub: string;
  title: string;
  context: string;
  options: string;
  decision: string;
  rationale: string;
  decider: string;
  decider_role: string;
  status: "offen" | "entschieden" | string;
  decision_date: string;
  links: DecisionLink[];
  meta: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
};

export type DecisionStats = { total: number; offen: number; entschieden: number };

export type DecisionListResponse = {
  tenant_id: string;
  count: number;
  status: string;
  q: string;
  items: Decision[];
  persistent: boolean;
  stats: DecisionStats;
};

export type DecisionInput = Partial<Omit<Decision, "id" | "created_at" | "updated_at" | "user_sub">>;

const KEY = {
  list: (t?: string | null, s?: string, q?: string) => ["decisions", t, s, q],
  stats: (t?: string | null) => ["decisions-stats", t],
};

export function useDecisions(status: "all" | "offen" | "entschieden" = "all", q = "") {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: KEY.list(effectiveTenantId, status, q),
    queryFn: () =>
      api.get<DecisionListResponse>(
        `/decisions?status=${status}&q=${encodeURIComponent(q)}&limit=200`,
      ),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 15_000,
  });
}

export function useCreateDecision() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DecisionInput) =>
      api.post<{ ok: boolean; decision: Decision }>("/decisions", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decisions"] });
      qc.invalidateQueries({ queryKey: ["decisions-stats"] });
      qc.invalidateQueries({ queryKey: ["next-actions"] });
    },
  });
}

export function useUpdateDecision() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DecisionInput }) =>
      api.put<{ ok: boolean; decision: Decision }>(`/decisions/${encodeURIComponent(id)}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decisions"] });
      qc.invalidateQueries({ queryKey: ["decisions-stats"] });
      qc.invalidateQueries({ queryKey: ["next-actions"] });
    },
  });
}

export function useSetDecisionStatus() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: "offen" | "entschieden" }) =>
      api.post<{ ok: boolean; decision: Decision }>(`/decisions/${encodeURIComponent(id)}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decisions"] });
      qc.invalidateQueries({ queryKey: ["decisions-stats"] });
      qc.invalidateQueries({ queryKey: ["next-actions"] });
    },
  });
}

export type AiSuggestResponse = {
  suggestion: string;
  rationale: string;
  engine: string;
  grounded: boolean;
  hinweis?: string;
};

export function useAiSuggest() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { title: string; context: string; options: string }) =>
      api.post<AiSuggestResponse>("/decisions/ai-suggest", input),
  });
}

export const STATUS_LABEL: Record<string, string> = {
  offen: "Offen",
  entschieden: "Entschieden",
};
